package com.example.positionahead.domain

import com.example.positionahead.model.PortfolioProjectionPoint
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.Quote
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.SymbolPerformance
import kotlin.math.pow

object PortfolioProjection {

    private val HORIZONS_MONTHS = listOf(3, 6, 12)

    /**
     * Compare two portfolios over 3 / 6 / 12 months using weighted recent returns,
     * FMP dividend yields, and monthly contributions. Illustrative only.
     */
    fun comparePortfolios(
        current: PortfolioSummary,
        target: PortfolioSummary,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
        monthlyContribution: Double,
    ): List<PortfolioProjectionPoint> {
        val currentTotal = current.totalMarketValue
        val targetTotal = target.totalMarketValue
        if (currentTotal <= 0 || targetTotal <= 0) return emptyList()

        val currentMonthlyReturn = estimatePortfolioMonthlyReturn(current, performance)
        val targetMonthlyReturn = estimatePortfolioMonthlyReturn(target, performance)
        val currentYield = blendedDividendYieldPercent(current, quotes)
        val targetYield = blendedDividendYieldPercent(target, quotes)
        val currentMonthlyDiv = currentYield / 100.0 / 12.0
        val targetMonthlyDiv = targetYield / 100.0 / 12.0

        return HORIZONS_MONTHS.map { months ->
            val currentValue = projectValueWithIncome(
                startValue = currentTotal,
                monthlyReturn = currentMonthlyReturn,
                monthlyDividendRate = currentMonthlyDiv,
                months = months,
                monthlyContribution = monthlyContribution,
            )
            val targetValue = projectValueWithIncome(
                startValue = targetTotal,
                monthlyReturn = targetMonthlyReturn,
                monthlyDividendRate = targetMonthlyDiv,
                months = months,
                monthlyContribution = monthlyContribution,
            )
            PortfolioProjectionPoint(
                months = months,
                currentPortfolioValue = currentValue,
                suggestedPortfolioValue = targetValue,
                deltaValue = targetValue - currentValue,
                currentEstimatedAnnualIncome = currentValue * currentYield / 100.0,
                suggestedEstimatedAnnualIncome = targetValue * targetYield / 100.0,
                currentBlendedYieldPercent = currentYield,
                suggestedBlendedYieldPercent = targetYield,
            )
        }
    }

    /** @deprecated Use [comparePortfolios]; kept for tests that reference trade-boost path. */
    fun compareCurrentVsSuggested(
        summary: PortfolioSummary,
        trades: List<RebalanceTrade>,
        performance: Map<String, SymbolPerformance>,
        monthlyContribution: Double,
    ): List<PortfolioProjectionPoint> {
        val total = summary.totalMarketValue
        if (total <= 0) return emptyList()

        val currentMonthlyReturn = estimatePortfolioMonthlyReturn(summary, performance)
        val suggestedMonthlyReturn = currentMonthlyReturn + rebalanceReturnBoost(trades)
        val yieldPct = blendedDividendYieldPercent(summary, emptyMap())
        val monthlyDiv = yieldPct / 100.0 / 12.0

        return HORIZONS_MONTHS.map { months ->
            val current = projectValueWithIncome(
                startValue = total,
                monthlyReturn = currentMonthlyReturn,
                monthlyDividendRate = monthlyDiv,
                months = months,
                monthlyContribution = monthlyContribution,
            )
            val suggested = projectValueWithIncome(
                startValue = total,
                monthlyReturn = suggestedMonthlyReturn,
                monthlyDividendRate = monthlyDiv,
                months = months,
                monthlyContribution = monthlyContribution,
            )
            PortfolioProjectionPoint(
                months = months,
                currentPortfolioValue = current,
                suggestedPortfolioValue = suggested,
                deltaValue = suggested - current,
                currentEstimatedAnnualIncome = current * yieldPct / 100.0,
                suggestedEstimatedAnnualIncome = suggested * yieldPct / 100.0,
                currentBlendedYieldPercent = yieldPct,
                suggestedBlendedYieldPercent = yieldPct,
            )
        }
    }

    fun blendedDividendYieldPercent(summary: PortfolioSummary, quotes: Map<String, Quote>): Double {
        if (summary.totalMarketValue <= 0) return 0.0
        var weighted = 0.0
        summary.positions.forEach { snapshot ->
            val weight = snapshot.marketValue / summary.totalMarketValue
            val symbol = snapshot.position.symbol.uppercase()
            val yield = quotes[symbol]?.dividendYield?.takeIf { it > 0 }
                ?: snapshot.estimatedAnnualIncome.takeIf { snapshot.marketValue > 0 }
                    ?.let { it / snapshot.marketValue * 100.0 }
                ?: 0.0
            weighted += weight * yield
        }
        return weighted.coerceIn(0.0, 25.0)
    }

    private fun estimatePortfolioMonthlyReturn(
        summary: PortfolioSummary,
        performance: Map<String, SymbolPerformance>,
    ): Double {
        if (summary.totalMarketValue <= 0) return 0.0
        var weighted = 0.0
        var weightSum = 0.0
        summary.positions.forEach { snapshot ->
            if (snapshot.marketValue <= 0) return@forEach
            val weight = snapshot.marketValue / summary.totalMarketValue
            weightSum += weight
            val perf = performance[snapshot.position.symbol.uppercase()]
            val annual = when {
                perf?.returnYtdPercent != null -> perf.returnYtdPercent / 100.0
                perf?.return1MonthPercent != null -> (perf.return1MonthPercent / 100.0) * 12.0
                else -> 0.07
            }
            weighted += weight * annual
        }
        if (weightSum <= 0) return monthlyRateFromAnnual(0.07)
        return monthlyRateFromAnnual((weighted / weightSum).coerceIn(-0.5, 0.5))
    }

    private fun rebalanceReturnBoost(trades: List<RebalanceTrade>): Double {
        if (trades.isEmpty()) return 0.0
        return 0.005 / 12.0
    }

    private fun monthlyRateFromAnnual(annualRate: Double): Double =
        (1.0 + annualRate).pow(1.0 / 12.0) - 1.0

    private fun projectValueWithIncome(
        startValue: Double,
        monthlyReturn: Double,
        monthlyDividendRate: Double,
        months: Int,
        monthlyContribution: Double,
    ): Double {
        var value = startValue
        repeat(months) {
            val growth = value * (1.0 + monthlyReturn)
            val dividends = value * monthlyDividendRate
            value = growth + dividends + monthlyContribution
        }
        return value
    }
}
